'use client';

import { createContext, useContext, type ComponentPropsWithRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { NdulgeLogoIcon, NdulgeDeluxeLogoIcon, AppleWalletMarkIcon, GoogleWalletMarkIcon } from '@/shared-ui/icons';
import { Button } from '@/shared-ui/atoms/button';
import { Typography, type TypographyProps } from '@/shared-ui/atoms/typography';

export type NdulgeCardRootVariant = 'ndulge' | 'deluxe';
export type NdulgeCardTier = 'ndulge' | 'deluxe';
export type NdulgeCardWalletProvider = 'apple' | 'google';

// Context — Root publishes variant so MemberBadge can read it without prop drilling.
const NdulgeCardContext = createContext<{ variant: NdulgeCardRootVariant }>({ variant: 'ndulge' });

// Root

interface RootProps extends ComponentPropsWithRef<'div'> {
    variant?: NdulgeCardRootVariant;
}

const Root = ({ variant = 'ndulge', className, ref, ...props }: RootProps) => (
    <NdulgeCardContext.Provider value={{ variant }}>
        <div
            ref={ref}
            data-slot="ndulge-card-root"
            data-variant={variant}
            className={cn(
                'relative flex min-h-[192px] w-full flex-col items-center justify-center gap-6 rounded-[14px] py-6 shadow-card text-snk-white',
                variant === 'deluxe' ? 'bg-snk-ndulge-deluxe' : 'bg-snk-ndulge',
                className
            )}
            {...props}
        />
    </NdulgeCardContext.Provider>
);
Root.displayName = 'NdulgeCard.Root';

// Greeting

interface GreetingProps extends ComponentPropsWithRef<'div'> {
    firstName: string;
    children?: ReactNode;
}

const Greeting = ({ firstName, children, className, ref, ...props }: GreetingProps) => {
    const { t } = useTranslation('ndulgeCard');
    return (
        <div
            ref={ref}
            data-slot="ndulge-card-greeting"
            className={cn('flex w-full flex-col items-center gap-2', className)}
            {...props}>
            <p className="font-serif text-[24px] leading-[30px] text-trim-cap text-snk-white text-center">
                {t('greeting', { name: firstName })}
            </p>
            {children}
        </div>
    );
};
Greeting.displayName = 'NdulgeCard.Greeting';

// MemberBadge

interface MemberBadgeProps extends ComponentPropsWithRef<'div'> {
    tier?: NdulgeCardTier;
}

const MemberBadge = ({ tier, className, ref, ...props }: MemberBadgeProps) => {
    const { t } = useTranslation('ndulgeCard');
    const { variant } = useContext(NdulgeCardContext);
    const resolvedTier = tier ?? variant;
    return (
        <div
            ref={ref}
            data-slot="ndulge-card-member-badge"
            className={cn('flex items-center gap-1 text-snk-white', className)}
            {...props}>
            {resolvedTier === 'deluxe' ? (
                <NdulgeDeluxeLogoIcon className="h-[11px]" />
            ) : (
                <NdulgeLogoIcon className="h-[11px]" />
            )}
            <p className="text-[14px] font-light whitespace-nowrap">{t('member')}</p>
        </div>
    );
};
MemberBadge.displayName = 'NdulgeCard.MemberBadge';

// MemberDetails

interface MemberDetailsProps extends ComponentPropsWithRef<'div'> {
    rewards: string;
    points: number;
    spendThreshold: string;
    rewardAmount: string;
}

const MemberDetails = ({
    rewards,
    points,
    spendThreshold,
    rewardAmount,
    className,
    ref,
    ...props
}: MemberDetailsProps) => {
    const { t } = useTranslation('ndulgeCard');
    return (
        <div
            ref={ref}
            data-slot="ndulge-card-member-details"
            className={cn('flex w-full flex-col gap-4', className)}
            {...props}>
            <div className="h-px w-full bg-snk-white" />

            <div className="flex w-full items-center justify-between">
                <div className="flex flex-1 flex-col items-center text-snk-white uppercase">
                    <p className="text-[20px] tracking-[2.4px]">{rewards}</p>
                    <p className="text-[16px] tracking-[1.44px]">{t('rewards')}</p>
                </div>

                <div className="w-px self-stretch bg-snk-white" />

                <div className="flex flex-1 flex-col items-center text-snk-white uppercase">
                    <p className="text-[20px] tracking-[2.4px]">{points}</p>
                    <p className="text-[16px] tracking-[1.44px]">{t('points')}</p>
                </div>
            </div>

            <p className="w-full text-center text-[14px] text-snk-white">
                <span className="font-light">{t('spendPrefix')} </span>
                <span className="font-normal">{spendThreshold}</span>
                <span className="font-light"> {t('spendMiddle')} </span>
                <span className="font-normal">{rewardAmount}</span>
                <span className="font-light"> {t('spendSuffix')}</span>
            </p>
        </div>
    );
};
MemberDetails.displayName = 'NdulgeCard.MemberDetails';

// WalletButton

interface WalletButtonProps extends ComponentPropsWithRef<'button'> {
    provider?: NdulgeCardWalletProvider;
}

const WalletButton = ({ provider = 'apple', className, ref, ...props }: WalletButtonProps) => {
    const { t } = useTranslation('ndulgeCard');
    return (
        <button
            ref={ref}
            {...props}
            type="button"
            data-slot="ndulge-card-wallet-button"
            aria-label={provider === 'google' ? t('walletAriaGoogle') : t('walletAriaApple')}
            className={cn(
                'absolute right-2 top-2 flex size-8 items-center justify-center rounded-full bg-snk-black hover:opacity-80 outline-none focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-2 focus-visible:ring-offset-snk-black',
                className
            )}>
            {provider === 'google' ? (
                <GoogleWalletMarkIcon className="h-4" aria-hidden />
            ) : (
                <AppleWalletMarkIcon className="h-4" aria-hidden />
            )}
        </button>
    );
};
WalletButton.displayName = 'NdulgeCard.WalletButton';

interface MessageProps extends Omit<TypographyProps, 'variant' | 'align' | 'as' | 'asChild'> {
    children: ReactNode;
}

const Message = ({ children, className, ref, ...props }: MessageProps) => (
    <Typography
        ref={ref}
        data-slot="ndulge-card-message"
        variant="p"
        align="center"
        className={cn('w-[263px]', className)}
        {...props}>
        {children}
    </Typography>
);
Message.displayName = 'NdulgeCard.Message';

// LinkAccountButton

interface LinkAccountButtonProps extends ComponentPropsWithRef<'button'> {
    children?: ReactNode;
}

const LinkAccountButton = ({ children, className, ref, ...props }: LinkAccountButtonProps) => {
    const { t } = useTranslation('ndulgeCard');
    return (
        <Button
            ref={ref}
            {...props}
            type="button"
            data-slot="ndulge-card-link-account-button"
            variant="tertiary"
            size="lg"
            className={cn('text-snk-white', className)}>
            {children ?? t('linkAccount')}
        </Button>
    );
};
LinkAccountButton.displayName = 'NdulgeCard.LinkAccountButton';

const NdulgeCard = { Root, Greeting, MemberBadge, MemberDetails, WalletButton, Message, LinkAccountButton } as const;
export { NdulgeCard };
